import { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import AuthContext from '../../context/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, loginWithGoogle, isAuthenticated, error, clearError } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
    
    // Clear any existing errors
    return () => {
      clearError();
    };
  }, [isAuthenticated, navigate, clearError]);
  
  const { name, email, password, confirmPassword } = formData;
  
  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error for this field
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: '' });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!name) {
      errors.name = 'Name is required';
    }
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await register({
        name,
        email,
        password
      });
      
      setIsSubmitting(false);
      
      if (result.success) {
        navigate('/');
      } else {
        // Display the specific error from the result
        console.error('Registration failed:', result.error);
        setFormErrors(prev => ({
          ...prev,
          general: result.error || 'Registration failed. Please try again.'
        }));
      }
    } catch (err) {
      setIsSubmitting(false);
      console.error('Registration error:', err);
      setFormErrors(prev => ({
        ...prev,
        general: 'An unexpected error occurred. Please try again.'
      }));
    }
  };
  
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { credential } = credentialResponse;
      
      // Decode the credential to get user info
      const base64Url = credential.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const { sub, email, name } = JSON.parse(jsonPayload);
      
      const result = await loginWithGoogle({
        googleId: sub,
        email,
        name
      });
      
      if (result.success) {
        navigate('/');
      }
    } catch (err) {
      console.error('Google login error:', err);
    }
  };
  
  const handleGoogleError = () => {
    console.error('Google login failed');
  };
  
  return (
    <div className="max-w-md mx-auto my-8">
      <div className="neumorph p-8 rounded-lg">
        <h2 className="text-2xl font-bold mb-6 text-center">Register</h2>
        
        {(error || formErrors.general) && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error || formErrors.general}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-gray-700 mb-2">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={handleChange}
              className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                formErrors.name ? 'border border-red-500' : ''
              }`}
              placeholder="Enter your name"
            />
            {formErrors.name && (
              <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 mb-2">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={handleChange}
              className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                formErrors.email ? 'border border-red-500' : ''
              }`}
              placeholder="Enter your email"
            />
            {formErrors.email && (
              <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 mb-2">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handleChange}
              className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                formErrors.password ? 'border border-red-500' : ''
              }`}
              placeholder="Enter your password"
            />
            {formErrors.password && (
              <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
            )}
          </div>
          
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-gray-700 mb-2">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
              className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                formErrors.confirmPassword ? 'border border-red-500' : ''
              }`}
              placeholder="Confirm your password"
            />
            {formErrors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{formErrors.confirmPassword}</p>
            )}
          </div>
          
          <button
            type="submit"
            className="neumorph-btn w-full py-2 px-4 rounded-lg text-gray-800 font-medium"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Registering...' : 'Register'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600 mb-4">Or register with</p>
          
          <div className="flex justify-center space-x-4">
            <div className="google-login-button">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
              />
            </div>
            
            <Link to="/login/phone" className="neumorph-btn py-2 px-4 rounded-lg text-gray-800">
              Phone
            </Link>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
